import React, { useState, useRef } from 'react'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import { getAiChatSubHeaderStyle } from 'src/utils/dnd/getItemStyle'
import { getAiChatSubHeaderListStyle } from 'src/utils/dnd/getSubHeaderListStyle'
import { withStyles } from '@material-ui/core/styles'
import EditIcon from '@material-ui/icons/EditOutlined'
import DeleteIcon from '@material-ui/icons/DeleteOutlined'
import DragIcon from '@material-ui/icons/DragIndicatorOutlined'
import { Box, Tooltip, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import styles from './styles'
import { CoreButton, CoreTextField } from 'src/components/core'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import AddSubheaderModal from 'src/components/add-subheader-modal'

const SubHeaders = props => {
  const dispatch = useDispatch()
  const [showTitleHelper, setShowTitleHelper] = useState(false)
  const modalRef = useRef(null)
  const {
    completeData,
    data,
    moduleIndex,
    handleSubHeaderItemChange,
    deleteSubheader,
    addOwnSubheader,
    setData,
    outlineId,
    classes,
  } = props

  const inputFocus = id => {
    document.getElementById(id).focus()
  }

  const onChangeSubheader = (e, moduleIndex, index) => {
    const newData = [...completeData.sections]
    newData[moduleIndex].modules[index].module_title = e.target.value
    setData({ ...completeData, modules: newData })
  }

  const handleAdd = selectedTitle => {
    if (selectedTitle) {
      if (!selectedTitle.text || selectedTitle.text.trim().length < 2) {
        if (modalRef.current) {
          modalRef.current.setShowError(true)
        }
        return
      }

      addOwnSubheader(moduleIndex)
      const newData = [...completeData.sections]
      const lastIndex = newData[moduleIndex].modules.length - 1

      newData[moduleIndex].modules[lastIndex] = {
        element_id: selectedTitle.element_id,
        module_title: selectedTitle.text,
        content_type: selectedTitle.content_type,
        attachment: {
          id: selectedTitle.attachment_id,
          name: selectedTitle.fileName,
          page_start: selectedTitle.page_start,
          page_end: selectedTitle.page_end,
          url: selectedTitle.url,
        },
      }

      setData({ ...completeData, sections: newData })
      dispatch(messageBoxHideAction())
    } else {
      // If no title is selected, show selection error
      if (modalRef.current) {
        modalRef.current.validateSelection()
      }
    }
  }

  const handleAddSubHeaderWithDocument = () => {
    let selectedTitle = null
    setShowTitleHelper(false)
    if (modalRef.current) {
      modalRef.current.resetState()
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Add',
              ButtonActionType.ADD,
              () => {
                handleAdd(selectedTitle)
              },
            ),
          ],
          title: 'Add a page',
          component: (
            <AddSubheaderModal
              ref={modalRef}
              outline={completeData}
              outlineId={outlineId}
              moduleIndex={moduleIndex}
              onTitleSelect={title => {
                selectedTitle = title
              }}
            />
          ),
        }),
      ),
    )
  }

  return (
    <Droppable droppableId={`droppable-${moduleIndex}`} type="MODULE">
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          style={getAiChatSubHeaderListStyle(snapshot.isDraggingOver)}
        >
          {data.map((module, index) => (
            <Draggable
              key={`${moduleIndex}-${index}`}
              draggableId={`${moduleIndex}-${index}`}
              index={index}
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  style={getAiChatSubHeaderStyle(
                    snapshot.isDragging,
                    provided.draggableProps.style,
                  )}
                >
                  <Box className={classes.header}>
                    <span
                      {...provided.dragHandleProps}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <DragIcon
                        color="inherit"
                        fontSize="small"
                        className={classes.subHeaderDragIcon}
                      />
                    </span>
                    <CoreTextField
                      id={`${moduleIndex}-${index}`}
                      key={`${moduleIndex}-${index}`}
                      defaultValue={module.module_title}
                      value={module.module_title}
                      variant="standard"
                      size="medium"
                      line={1}
                      maxRows={3}
                      style={{ borderBottom: 0, minWidth: '65%' }}
                      onBlur={e =>
                        handleSubHeaderItemChange(e, moduleIndex, index)
                      }
                      onChange={e => onChangeSubheader(e, moduleIndex, index)}
                      placeholder="Edit Your Outline Subheader"
                      multiline
                    />
                    <div className={classes.flexGrow}></div>

                    <span className={classes.subHeaderButtons}>
                      <EditIcon
                        onClick={() => inputFocus(`${moduleIndex}-${index}`)}
                        fontSize="small"
                        className={classes.button}
                      />
                      <DeleteIcon
                        onClick={() => deleteSubheader(moduleIndex, index)}
                        fontSize="small"
                        className={classes.button}
                      />
                    </span>

                    {module.attachment && (
                      <Tooltip
                        title=<Typography
                          dangerouslySetInnerHTML={{
                            __html: `File: ${module.attachment.name} <br/> Chapter: ${module.element_id}`,
                          }}
                          fontSize="12px"
                          fontWeight={400}
                          color="#fff"
                        />
                      >
                        <div className={classes.document}>
                          <Icon
                            icon="lets-icons:file-fill"
                            className={classes.fileIcon}
                            color="#002E47"
                          ></Icon>
                          <span className={classes.documentName}>Info</span>
                        </div>
                      </Tooltip>
                    )}
                  </Box>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          <CoreButton
            variant="text"
            size="small"
            color="disabled"
            onClick={() => handleAddSubHeaderWithDocument()}
          >
            <AddIcon
              fontSize="small"
              style={{
                color: '#637381',
              }}
            />
            Add a Page
          </CoreButton>
        </div>
      )}
    </Droppable>
  )
}

export default withStyles(styles)(SubHeaders)
